import React, { useCallback, useState } from 'react';

import { useMutateOrganisationProjects } from '@hooks/portal/projects/organisationProjects/useMutateOrganisationProjects';
import { useQueryOrganisationProjects } from '@hooks/portal/projects/organisationProjects/useQueryOrganisationProjects';
import {
	useQueryOrganisationProjectAdmins,
} from '@hooks/portal/projects/organisationProjects/useQueryOrganisationProjectsAdmins';
import {
	useQueryOrganisationProjectMembers,
} from '@hooks/portal/projects/organisationProjects/useQueryOrganisationProjectsMembers';
import {
	useQueryOrganisationProjectStatuses,
} from '@hooks/portal/projects/organisationProjects/useQueryOrganisationProjectsStatuses';
import {
	useQueryOrganisationProjectWorkflows,
} from '@hooks/portal/projects/organisationProjects/useQueryOrganisationProjectsWorkflows';

import { organisationProjects } from '@store/selectors';
import { useAppSelector } from '@store/store';

import ProjectsTable from '../ProjectsTable';

interface IPlatformOwnerProjectsContainer {
	organizationId: string;
}

const OrganisationAdminProjectsContainer = ({
	organizationId,
}: IPlatformOwnerProjectsContainer) => {
	const {
		searchValue,
		status,
	} = useAppSelector(organisationProjects);

	const [isShowDeleteProjectsConfirm, setIsShowDeleteProjectsConfirm] = useState(false);
	const [singleProjectIdForRemove, setSingleProjectIdForRemove] = useState<string | null>(null);

	const openDeleteProjectsConfirm = useCallback(() => {
		setIsShowDeleteProjectsConfirm(prevState => !prevState);
	}, []);

	const closeDeleteProjectsConfirm = useCallback(() => {
		setIsShowDeleteProjectsConfirm(false);
		setSingleProjectIdForRemove(null);
	}, []);

	const {
		projectsList,
		isLoadingProjects,
		countOfRecords,
	} = useQueryOrganisationProjects(organizationId);

	const { projectsStatuses, isLoadingProjectsStatuses } = useQueryOrganisationProjectStatuses();
	const { projectsWorkflows, isLoadingProjectsWorkflows } = useQueryOrganisationProjectWorkflows();
	const { projectAdmins, isLoadingProjectsAdmins } = useQueryOrganisationProjectAdmins(organizationId);
	const { projectMembers, isLoadingProjectsMembers } = useQueryOrganisationProjectMembers(organizationId);

	const {
		createProject,
		updateProject,
		handleDeleteProject,
		handleDeleteProjects,
		isLoadingDelete,
		handleCheckProjectCode,
	} = useMutateOrganisationProjects({
		organizationId,
		searchValue,
		status,
		closeDeleteProjectsConfirm,
	});

	const isLoading = isLoadingProjects
		|| isLoadingProjectsStatuses
		|| isLoadingProjectsWorkflows
		|| isLoadingProjectsAdmins
		|| isLoadingProjectsMembers;

	return (
		<ProjectsTable
			closeDeleteProjectsConfirm={closeDeleteProjectsConfirm}
			countOfRecords={countOfRecords}
			createProject={createProject}
			handleCheckProjectCode={handleCheckProjectCode}
			handleDeleteProject={handleDeleteProject}
			handleDeleteProjects={handleDeleteProjects}
			isLoading={isLoading}
			isLoadingDelete={isLoadingDelete}
			isShowDeleteProjectsConfirm={isShowDeleteProjectsConfirm}
			openDeleteProjectsConfirm={openDeleteProjectsConfirm}
			organisationId={organizationId}
			projectAdmins={projectAdmins}
			projectMembers={projectMembers}
			projectsList={projectsList}
			projectsStatuses={projectsStatuses}
			projectsWorkflows={projectsWorkflows}
			setSingleProjectIdForRemove={setSingleProjectIdForRemove}
			singleProjectIdForRemove={singleProjectIdForRemove}
			updateProject={updateProject}
		/>
	);
};

export default OrganisationAdminProjectsContainer;
